import isEmpty from 'lodash/isEmpty';
import React, { useCallback } from 'react';
import ReactDOM from 'react-dom';

import { useAdvancedFiltersTrigger } from '@tanium/react-advanced-filters-trigger-context';
import { Box } from '@tanium/react-box';
import { Button } from '@tanium/react-button';
import { CircleIcon } from '@tanium/react-composed-icons';
import { ControlGroup } from '@tanium/react-control-group';
import { Disclosure } from '@tanium/react-disclosure';
import styled from '@tanium/react-emotion-9';
import { BlockedIcon } from '@tanium/react-graphics';
import { Stack, StackChild } from '@tanium/react-stack';
import { useTheme } from '@tanium/react-theme-context';
import { ToggleButton } from '@tanium/react-toggle-button';
import {
  CollapsedProps,
  OnToggleCollapsedEventHandler,
  useCollapsed,
} from '@tanium/react-use-collapsed';
import { useControlledProp } from '@tanium/react-use-controlled-prop';
import { useRandomId } from '@tanium/react-use-random-id';
import { css, SpaceStyleProps } from '@tanium/style-system';
import { ThemeFontWeights, ThemeSpace } from '@tanium/theme-data';

import useAdvancedFilters, { UseAdvancedFiltersProps } from '../hooks/useAdvancedFilters';
import { Trans, useTranslation } from '../localization';
import {
  Fill,
  HeaderBottomBorder,
  HeaderFill,
  ShowFiltersToggle,
  UnavailableFill,
  UnavailableForeground,
} from '../themeDefinitions';
import AdvancedFiltersContext from './AdvancedFiltersContext';
import FilterPills from './FilterPills';

const HEADER_HEIGHT = 32;
export const COMMON_ATTRIBUTES = 'commonAttributes';
export const FILTER_BUILDER = 'filterBuilder';

type View = 'commonAttributes' | 'filterBuilder';
interface OnFilterViewChangeEvent {
  view: View;
}

export interface Props extends CollapsedProps, UseAdvancedFiltersProps {
  /**
   * Whether or not the component should be collapsed. If you want to manage the collapse state, you
   * may provide a value here. This will prevent the AdvancedFilters component from managing the
   * collapse state. The `onToggleCollapsed` prop is used to handle toggling expand / collapse
   * state manually.
   */
  isCollapsed?: boolean;
  /**
   * Callback for whenever the AdvancedFilters is collapsed or expanded.
   */
  onToggleCollapsed?: OnToggleCollapsedEventHandler;
  /**
   * Content to display within the AdvancedFilters as Standard.
   */
  children?: React.ReactNode;
  /**
   * Filter Builder content to display within the AdvancedFilters.
   */
  filterBuilder?: React.ReactNode;
  /**
   * Callback for whenever the AdvancedFilters view changes between Standard and Filter Builder.
   */
  onFilterViewChange?: (event: OnFilterViewChangeEvent) => void;
  /**
   * Sets the default AdvancedFilters view: `commonAttributes` (Standard) or `filterBuilder`.
   * If filterView prop is provided, this value is ignored.
   */
  defaultFilterView?: View;
  /**
   * Sets the AdvancedFilters view: `commonAttributes` (Standard) or `filterBuilder`.
   * If you want to manage the filterView state, you may provide a value here.
   */
  filterView?: View;
}

const Wrapper = styled('div')(
  css({
    borderTopLeftRadius: 1,
    borderTopRightRadius: 1,
  }),
);

const Header = styled(Stack)(
  css({
    bg: Fill,
    borderTopLeftRadius: 1,
    borderTopRightRadius: 1,
    minHeight: HEADER_HEIGHT,
    px: 1,
    lineHeight: 1,
    userSelect: 'none',
  }),
);

const ToggleWrapper = styled('div')(
  css({
    cursor: 'pointer',
    my: 1,
  }),
);

const FiltersLabel = styled('span')(
  css({
    ml: ThemeSpace.minor[1],
    fontSize: '12px',
    fontWeight: ThemeFontWeights.Bold,
    color: ShowFiltersToggle,
  }),
);

const StyledButton = styled(Button)(
  css({
    fontSize: '12px',
    fontWeight: ThemeFontWeights.Bold,
    my: 1,
  }),
);

const StyledDisclosure = styled(Disclosure, { shouldForwardProp: (p) => p !== 'isCollapsed' })(
  css({
    color: ShowFiltersToggle,
  }),
);

const StyledPanel = styled('div')(
  css({
    bg: Fill,
    border: 0,
    borderRadius: 0,

    pb: 2,
    px: 1,
  }),
);

const BlockCircleIcon = styled(CircleIcon)({ display: 'block' });

const Separator = ({ mb = 0 }: { mb?: SpaceStyleProps['mb'] }) => (
  <Box
    as="hr"
    border={0}
    borderBottom={1}
    borderColor={HeaderBottomBorder}
    p={0}
    m={0}
    mb={mb}
    height="1px"
    bg={HeaderFill}
  />
);

const PaddedBox = styled(Box)(
  css({
    px: 1,
  }),
);

/**
 * Gets a valid filter view from a potentially-invalid filter view. Invalid filter views
 * are treated as 'commonAttributes'
 * @param view The potentially-invalid filter view.
 */
const getValidFilterView = (view?: string): View =>
  view === FILTER_BUILDER ? view : COMMON_ATTRIBUTES;

export const UnavailableCommonAttributes = () => {
  const theme = useTheme();
  return (
    <Stack
      bg={UnavailableFill}
      color={UnavailableForeground}
      borderRadius={1}
      p={2}
      fontSize="12px"
      itemSpacing={1}
      justifyContent="center"
      flexGrow={1}
    >
      <BlockCircleIcon
        innerRadius={8}
        outerRadius={8}
        iconColor={UnavailableFill(theme)}
        backgroundColor={UnavailableForeground(theme)}
        icon={BlockedIcon}
      />
      <Trans>
        Standard filtering is unavailable when a <b>Filter Builder</b> is applied
      </Trans>
    </Stack>
  );
};

const AdvancedFilterTrigger = ({
  isCollapsed,
  setIsCollapsed,
}: {
  isCollapsed: boolean;
  setIsCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const { portalElement } = useAdvancedFiltersTrigger();
  const { t } = useTranslation();
  return portalElement
    ? ReactDOM.createPortal(
        <StyledButton
          variant="link"
          size="sm"
          onClick={() => {
            setIsCollapsed(!isCollapsed);
          }}
        >
          <Stack itemSpacing={1}>
            <Disclosure isCollapsed={isCollapsed} />
            {t('Filters')}
          </Stack>
        </StyledButton>,
        portalElement,
      )
    : null;
};

const AdvancedFilters = ({
  isCollapsed: isCollapsedProp,
  defaultCollapsed = true,
  onToggleCollapsed,
  value: valueProp,
  defaultValue,
  onChange,
  children,
  filterBuilder,
  onFilterViewChange,
  defaultFilterView,
  filterView: filterViewProp,
}: Props) => {
  const { t } = useTranslation();
  const prefix = useRandomId();
  const { clear, value, pills, updateFilter, drafts } = useAdvancedFilters({
    defaultValue,
    onChange,
    value: valueProp,
  });
  const { collapseControlProps, isCollapsed, setIsCollapsed } = useCollapsed(
    { defaultCollapsed, onToggleCollapsed, isCollapsible: true, isCollapsed: isCollapsedProp },
    'AdvancedFilters',
  );

  const defaultView = getValidFilterView(defaultFilterView);
  const [filterView, setFilterView] = useControlledProp({
    defaultValue: defaultView,
    componentName: 'AdvancedFilters',
    onUpdate: ({ value: updatedValue }) => {
      if (onFilterViewChange) {
        onFilterViewChange({ view: updatedValue });
      }
    },
    propName: 'filterView',
    value: filterViewProp,
  });

  const handleFilterViewClick = useCallback(
    (newView: View) => {
      if (newView !== filterView) {
        setFilterView(newView);
      }
    },
    [filterView, setFilterView],
  );

  const filterBuilderIsApplied = !!value.filterBuilder;
  const showCommonAttributes = filterView === COMMON_ATTRIBUTES || !filterBuilder;
  const showFilterBuilder = filterView === FILTER_BUILDER || !children;

  const { portalElement } = useAdvancedFiltersTrigger();
  const showHeader = !portalElement || !isEmpty(value);

  const showContent = showHeader || !isCollapsed;
  return (
    <>
      <AdvancedFilterTrigger isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} />
      <AdvancedFiltersContext.Provider
        value={{
          clear,
          prefix,
          value,
          drafts,
          updateFilterValue: updateFilter,
        }}
      >
        {showContent && (
          <Wrapper>
            {showHeader && (
              <Header itemShrink={0} itemSpacing={1} alignItems="center">
                <ToggleWrapper
                  {...collapseControlProps}
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsCollapsed(!isCollapsed);
                  }}
                  onKeyPress={(e) => {
                    e.stopPropagation();
                    if (e.key === 'Enter' || e.key === ' ') {
                      setIsCollapsed(!isCollapsed);
                      e.preventDefault();
                    }
                  }}
                >
                  <StyledDisclosure isCollapsed={isCollapsed} />
                  <FiltersLabel>{t('Filters')}</FiltersLabel>
                </ToggleWrapper>
                <StackChild grow={1} shrink={1}>
                  <FilterPills pills={pills} updateFilter={updateFilter} value={value} />
                </StackChild>
                {!isEmpty(value) && (
                  <StyledButton variant="link" onClick={clear}>
                    {t('Clear Filters')}
                  </StyledButton>
                )}
              </Header>
            )}
            {!isCollapsed && (
              <StyledPanel>
                <Separator mb={1} />
                <Stack direction="column" itemSpacing={1}>
                  {filterBuilder &&
                    children && [
                      <PaddedBox key="toggle">
                        <ControlGroup>
                          <ToggleButton
                            size="sm"
                            selected={showCommonAttributes}
                            onToggle={() => {
                              handleFilterViewClick(COMMON_ATTRIBUTES);
                            }}
                          >
                            {t('Standard')}
                          </ToggleButton>
                          <ToggleButton
                            size="sm"
                            selected={showFilterBuilder}
                            onToggle={() => {
                              handleFilterViewClick(FILTER_BUILDER);
                            }}
                          >
                            {t('Filter Builder')}
                          </ToggleButton>
                        </ControlGroup>
                      </PaddedBox>,
                      <Separator key="separator" />,
                    ]}
                  {showCommonAttributes && children && (
                    <Box px={1} display="flex" flexWrap="wrap">
                      {filterBuilderIsApplied ? <UnavailableCommonAttributes /> : children}
                    </Box>
                  )}
                  {showFilterBuilder && filterBuilder && <PaddedBox>{filterBuilder}</PaddedBox>}
                </Stack>
              </StyledPanel>
            )}
          </Wrapper>
        )}
      </AdvancedFiltersContext.Provider>
    </>
  );
};

export default AdvancedFilters;
